import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { ChannelEnum, TemplateEnum } from '@ibk/shared-types';
import { maskEmail, maskPhone } from '@ibk/shared-utils';
import { useHasValidTokenMutation, useSendEmailTokenMutation, useSendPhoneTokenMutation, useSendWhatsappTokenMutation, } from '../../../services';
import { useAccountData } from '../../hooks/useAccountData';
export function useTokenValidation({ source = 'email', onContinue }) {
    const { t } = useTranslation();
    const { email, cpf, deviceId, phone, maskedRecipient, setMaskedRecipient } = useAccountData();
    const [token, setToken] = useState(Array(6).fill(''));
    const [countdown, setCountdown] = useState(60);
    const [isCounting, setIsCounting] = useState(true);
    const [tokenError, setTokenError] = useState(undefined);
    const [sendEmailToken, { isLoading: isResendingEmail }] = useSendEmailTokenMutation();
    const [sendPhoneToken, { isLoading: isResendingPhone }] = useSendPhoneTokenMutation();
    const [sendWhatsappToken, { isLoading: isResendingWhatsapp }] = useSendWhatsappTokenMutation();
    const [hasValidToken, { isLoading: isValidating }] = useHasValidTokenMutation();
    const maskedEmail = maskedRecipient ?? maskEmail(email);
    const maskedPhone = maskPhone(phone);
    const isPhoneSource = source === 'phone';
    useEffect(() => {
        setToken(Array(6).fill(''));
        setTokenError(undefined);
        setCountdown(60);
        setIsCounting(true);
    }, [source]);
    useEffect(() => {
        if (!isCounting)
            return;
        const timer = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    setIsCounting(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [isCounting]);
    const handleTokenChange = useCallback((index, value) => {
        const upperValue = value.toUpperCase();
        if (!/^[A-Z0-9]*$/.test(upperValue))
            return;
        setTokenError(prev => (prev ? undefined : prev));
        setToken(prev => {
            const newToken = [...prev];
            newToken[index] = upperValue.slice(-1);
            return newToken;
        });
        if (upperValue && index < 5) {
            const nextInput = document.getElementById(`token-input-${source}-${index + 1}`);
            nextInput?.focus();
        }
    }, [source]);
    const handleKeyDown = useCallback((index, e) => {
        if (e.key === 'Backspace' && !token[index] && index > 0) {
            const prevInput = document.getElementById(`token-input-${source}-${index - 1}`);
            prevInput?.focus();
        }
    }, [token, source]);
    const handlePaste = useCallback((e) => {
        e.preventDefault();
        const pastedData = e.clipboardData
            .getData('text')
            .toUpperCase()
            .replace(/[^A-Z0-9]/g, '')
            .slice(0, 6);
        const newToken = Array(6).fill('');
        pastedData.split('').forEach((char, index) => {
            if (index < 6) {
                newToken[index] = char;
            }
        });
        setToken(newToken);
    }, []);
    const isComplete = token.every(digit => digit !== '') && token.join('').length === 6;
    const handleContinue = useCallback(async () => {
        if (!isComplete || !deviceId || !cpf) {
            return;
        }
        setTokenError(undefined);
        try {
            const cleanDocumentNumber = cpf.replace(/\D/g, '');
            const tokenString = token.join('');
            await hasValidToken({
                token: tokenString,
                deviceId,
                templateId: TemplateEnum.VALIDATION_TOKEN_TEMPLATE,
                documentNumber: cleanDocumentNumber,
            }).unwrap();
            await onContinue();
        }
        catch {
            setTokenError(t('onboarding.token.invalid'));
        }
    }, [isComplete, deviceId, cpf, token, hasValidToken, onContinue, t]);
    const handleResendCode = useCallback(async () => {
        if (!deviceId || !cpf) {
            return;
        }
        try {
            const cleanDocumentNumber = cpf.replace(/\D/g, '');
            if (isPhoneSource) {
                await sendPhoneToken({
                    deviceId,
                    templateId: TemplateEnum.VALIDATION_TOKEN_TEMPLATE,
                    documentNumber: cleanDocumentNumber,
                    channel: ChannelEnum.phone,
                }).unwrap();
            }
            else {
                const result = await sendEmailToken({
                    deviceId,
                    templateId: TemplateEnum.VALIDATION_TOKEN_TEMPLATE,
                    documentNumber: cleanDocumentNumber,
                    channel: ChannelEnum.email,
                }).unwrap();
                const apiMaskedRecipient = result?.result?.maskedRecipient;
                if (apiMaskedRecipient) {
                    setMaskedRecipient(apiMaskedRecipient);
                }
            }
            setCountdown(60);
            setIsCounting(true);
        }
        catch {
        }
    }, [deviceId, cpf, isPhoneSource, sendPhoneToken, sendEmailToken, setMaskedRecipient]);
    const handleResendWhatsapp = useCallback(async () => {
        if (!deviceId || !cpf) {
            return;
        }
        try {
            const cleanDocumentNumber = cpf.replace(/\D/g, '');
            await sendWhatsappToken({
                deviceId,
                templateId: TemplateEnum.VALIDATION_TOKEN_TEMPLATE,
                documentNumber: cleanDocumentNumber,
                channel: ChannelEnum.phone,
            }).unwrap();
            setCountdown(60);
            setIsCounting(true);
        }
        catch {
        }
    }, [deviceId, cpf, sendWhatsappToken]);
    return {
        token,
        countdown,
        isCounting,
        tokenError,
        isComplete,
        maskedEmail,
        maskedPhone,
        isPhoneSource,
        isResendingEmail,
        isResendingPhone,
        isResendingWhatsapp,
        isValidating,
        handleTokenChange,
        handleKeyDown,
        handlePaste,
        handleContinue,
        handleResendCode,
        handleResendWhatsapp,
    };
}
